/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { useState } from 'react';
import { Box } from '@mui/material';
import { languages } from '../../Internationalisierung/languages';
import useErrors from '../../Utils/userErrors';
import withTooltip from '../HOC/withTooltip';
import { Sprachcode } from '../LokalisierterTextInput';
import RichTextInput from '../RichTextInput';
import RichTextInputProps from '../RichTextInput/RichTextInputProps';
import { smallRichTextInputHeight } from '../SmallRichTextInput';
import SpracheSelect from '../SpracheSelect';

type LokalisierterRichTextInputValues = { [land in Sprachcode]: string }
type LokalisierterRichTextInputFeldBezeichnungen = { [land in Sprachcode]: string }
type LokalisierterRichTextInputCaptions = { [land in Sprachcode]: string }

interface LokalisierterRichTextInputProps extends Omit<RichTextInputProps, "caption" | "feldBezeichnung" | "value" | "onChange"> {
    captions: LokalisierterRichTextInputCaptions;
    feldBezeichnungen: LokalisierterRichTextInputFeldBezeichnungen;
    values: LokalisierterRichTextInputValues;
    onChange: (newValues: LokalisierterRichTextInputValues) => void;
    isSmall?: boolean;
}

export default withTooltip(function LokalisierterRichTextInput(props: LokalisierterRichTextInputProps) {
    const { error: errorDe, errorTexts: errorTextsDe, clearErrorsOnChange: clearErrorsOnChangeDe } = useErrors({ ...props, feldBezeichnung: props.feldBezeichnungen.de });
    const { error: errorEn, errorTexts: errorTextsEn, clearErrorsOnChange: clearErrorsOnChangeEn } = useErrors({ ...props, feldBezeichnung: props.feldBezeichnungen.en });

    const [sprache, setzeSprache] = useState<Sprachcode>("de");
    const [values, setzeValues] = useState(props.values);

    const textInputProps = {
        ...((props.isSmall ?? false) && { height: smallRichTextInputHeight }),
        ignoreTooltip: true,
        withTooltipStyle: {
            width: "100%",
        },
        sx: { flex: 1 },
        lokalisierbar: true,
    }

    React.useEffect(() => {
        if (errorDe) setzeSprache("de")
        if (errorEn) setzeSprache("en")
    }, [errorDe, errorEn])

    return (
        <Box sx={{ display: "flex", flexDirection: "row" }}>
            {(() => {
                switch (sprache) {
                    case languages.de:
                        return <RichTextInput
                            {...props}
                            {...textInputProps}
                            caption={props.captions.de}
                            typBezeichnung={props.typBezeichnung}
                            feldBezeichnung={props.feldBezeichnungen.de}
                            value={values.de}
                            errors={errorTextsDe}
                            onChange={clearErrorsOnChangeDe((e) => {
                                const newState: LokalisierterRichTextInputValues = {
                                    ...props.values,
                                    de: e
                                }
                                setzeValues(newState);
                                props.onChange(newState);
                            })}
                        />

                    case languages.en:
                        return <RichTextInput
                            {...props}
                            {...textInputProps}
                            caption={props.captions.en}
                            typBezeichnung={props.typBezeichnung}
                            feldBezeichnung={props.feldBezeichnungen.en}
                            value={values.en}
                            errors={errorTextsEn}
                            onChange={clearErrorsOnChangeEn((e) => {
                                const newState: LokalisierterRichTextInputValues = {
                                    ...props.values,
                                    en: e
                                }
                                setzeValues(newState);
                                props.onChange(newState);
                            })}
                        />
                }
            })()}

            <SpracheSelect
                initialeSprache={sprache}
                buttonProps={{
                    color: "secondary",
                    variant: "contained",
                    size: "small",
                    sx: {
                        alignSelf: "start",
                        mt: 1,
                        lineHeight: 1,
                        padding: ".5rem",
                        height: "40px",
                        fontSize: "1rem",
                        boxShadow: "none",
                        "&:hover": { boxShadow: "none" },
                    }
                }}
                onChange={(e) => setzeSprache(e.sprachcode as Sprachcode)}
            />
        </Box>
    );
});
